import React, { useCallback, useEffect, useState } from 'react';
import { BarChart3, Crown, Search } from 'lucide-react';
import Spinner from '../../components/UI/Spinner';
import EmptyState from '../../components/UI/EmptyState';
import { AddressLink, StatTile, btc, monthRange, usd } from './components';
import { MONTHLY_PRIZE_SPLIT, SCORING } from './config';
import { useT } from '../../i18n';

const PAGE_SIZE = 25;

const RankCell = ({ rank }) => {
  const tone =
    rank === 1
      ? 'border-amber-400/40 bg-amber-400/10 text-amber-300'
      : rank === 2
      ? 'border-white/25 bg-white/10 text-white'
      : rank === 3
      ? 'border-orange-400/30 bg-orange-400/10 text-orange-300'
      : 'border-white/10 bg-white/5 text-white/50';

  return (
    <span
      className={`notranslate lb-num inline-flex h-8 w-8 items-center justify-center rounded-full border text-sm font-bold ${tone}`}
    >
      {rank}
    </span>
  );
};

/**
 * All-time and monthly standings.
 *
 * The monthly board is what pays: the accumulated WBTC pot is split 50/30/20
 * between the top three when the month closes.
 */
const Leaderboard = ({ game }) => {
  const t = useT();
  const { contract, month, monthlyPot, address } = game;

  const [scope, setScope] = useState('alltime');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const raw =
        scope === 'monthly'
          ? await contract.getMonthlyLeaderboard(month, 0, PAGE_SIZE).catch(() => [])
          : await contract.getLeaderboard(0, PAGE_SIZE).catch(() => []);

      setEntries(
        (raw || []).map((entry, index) => ({
          player: entry.player || entry[0],
          score: Number((entry.score || entry[1]).toString()),
          rank: entry.rank ? Number(entry.rank.toString()) : index + 1,
        }))
      );
    } catch (err) {
      console.error('[BullsEye] leaderboard read failed', err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [contract, scope, month]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = query
    ? entries.filter((entry) => entry.player.toLowerCase().includes(query.toLowerCase()))
    : entries;

  const mine = address
    ? entries.find((entry) => entry.player.toLowerCase() === address.toLowerCase())
    : null;

  return (
    <div className="space-y-6">
      <section className="lb-section p-6 sm:p-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <span className="lb-chip lb-chip-gold">
              <Crown className="h-3.5 w-3.5" aria-hidden="true" />
              {t('bullseye.lb.standings')}
            </span>
            <h2 className="lb-heading mt-4 text-4xl sm:text-5xl">{t('bullseye.tabLeaderboard')}</h2>
            <p className="mt-3 max-w-xl text-sm text-white/70">
              Points are earned for accuracy and for showing up. The monthly board pays out in
              WBTC.
            </p>
          </div>

          <div className="lb-seg">
            {[
              { key: 'alltime', label: 'bullseye.lb.allTime' },
              { key: 'monthly', label: 'bullseye.lb.thisMonth' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setScope(tab.key)}
                data-active={scope === tab.key}
                data-tone="gold"
                className="lb-seg-item"
              >
                {t(tab.label)}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile label={t('bullseye.lb.monthlyPot')} value={`${btc(monthlyPot)} WBTC`} tone="gold" />
          <StatTile label={t('bullseye.lb.rankedPlayers')} value={usd(entries.length)} />
          <StatTile
            label={t('bullseye.lb.yourRank')}
            value={mine ? `#${mine.rank}` : address ? 'Unranked' : '-'}
            sub={mine ? `${usd(mine.score)} points` : null}
            tone="gradient"
          />
          <StatTile
            label={t('bullseye.lb.period')}
            value={scope === 'monthly' ? monthRange(month) : t('bullseye.lb.allTime')}
            sub={scope === 'monthly' ? `Month ${month}` : null}
          />
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[1.55fr,1fr]">
        <section className="lb-card p-5 sm:p-6">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <h3 className="lb-heading flex items-center gap-2 text-xl">
              <BarChart3 className="h-5 w-5 text-brand-cyan" aria-hidden="true" />
              {scope === 'monthly' ? 'Monthly ranking' : 'All-time ranking'}
            </h3>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30"
                aria-hidden="true"
              />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('bullseye.lb.searchAddress')}
                aria-label={t('bullseye.lb.searchAddress')}
                className="lb-input notranslate w-full pl-9 text-sm sm:w-60"
              />
            </div>
          </div>

          {loading ? (
            <div className="py-16">
              <Spinner label={t('bullseye.lb.loading')} />
            </div>
          ) : filtered.length ? (
            <div className="lb-scroll overflow-x-auto">
              <table className="w-full min-w-[420px] text-left text-sm">
                <thead>
                  <tr className="text-[0.65rem] uppercase tracking-[0.16em] text-white/45">
                    <th className="px-3 py-3 font-normal">{t('bullseye.lb.rank')}</th>
                    <th className="px-3 py-3 font-normal">{t('bullseye.player')}</th>
                    <th className="px-3 py-3 text-right font-normal">{t('bullseye.lb.score')}</th>
                    <th className="px-3 py-3 text-right font-normal">{t('bullseye.lb.prizeShare')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((entry) => {
                    const share = MONTHLY_PRIZE_SPLIT[entry.rank - 1];
                    const isMine =
                      address && entry.player.toLowerCase() === address.toLowerCase();

                    return (
                      <tr
                        key={entry.player}
                        className={`border-t border-white/5 transition ${
                          isMine ? 'bg-brand-cyan/5 text-white' : 'text-white/80 hover:bg-white/[0.03]'
                        }`}
                      >
                        <td className="px-3 py-3">
                          <RankCell rank={entry.rank} />
                        </td>
                        <td className="px-3 py-3">
                          <AddressLink value={entry.player} />
                          {isMine ? (
                            <span className="ml-2 text-[0.6rem] uppercase tracking-[0.16em] text-brand-cyan">
                              you
                            </span>
                          ) : null}
                        </td>
                        <td className="notranslate lb-num px-3 py-3 text-right font-bold text-white">
                          {usd(entry.score)}
                        </td>
                        <td className="notranslate lb-num px-3 py-3 text-right">
                          {scope === 'monthly' && share ? (
                            <span className="font-bold text-amber-300">
                              {share}% · {btc((monthlyPot * share) / 100)} WBTC
                            </span>
                          ) : (
                            <span className="text-white/30">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              icon={Crown}
              title={t('bullseye.lb.noStandings')}
              message={
                query
                  ? 'No player matches that address.'
                  : 'Nobody has scored in this period yet. Place a prediction to get on the board.'
              }
            />
          )}
        </section>

        <div className="space-y-6">
          <section className="lb-card p-5 sm:p-6">
            <h3 className="lb-heading mb-5 text-xl">{t('bullseye.lb.monthlyPrizes')}</h3>
            <ul className="space-y-3">
              {MONTHLY_PRIZE_SPLIT.map((share, index) => (
                <li
                  key={share}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3"
                >
                  <span className="flex items-center gap-3">
                    <RankCell rank={index + 1} />
                    <span className="text-sm text-white/60">
                      {index === 0 ? '1st place' : index === 1 ? '2nd place' : '3rd place'}
                    </span>
                  </span>
                  <span className="text-right">
                    <span className="notranslate lb-num block font-bold text-white">{share}%</span>
                    <span className="notranslate lb-num block text-xs text-amber-300">
                      {btc((monthlyPot * share) / 100)} WBTC
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-white/45">
              The pot grows with every bet placed during the month and is paid when the month
              closes.
            </p>
          </section>

          <section className="lb-card p-5 sm:p-6">
            <h3 className="lb-heading mb-5 text-xl">{t('bullseye.lb.howPointsWork')}</h3>
            <ul className="space-y-2.5">
              {SCORING.map((rule) => (
                <li key={rule.label} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-white/60">{t(rule.label)}{rule.suffix || ''}</span>
                  <span className="notranslate lb-num font-bold text-white">+{rule.points}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
